import { combineReducers } from 'redux';
import InternalJobsReducer from './InternalJobs/reducers/InternalJobsReducer';
import SurveyLibraryReducer from './SurveyLibrary/reducers/SurveyLibraryReducer';
import JobPricingReducer from './JobPricing/JobPricingReducer';
import SlottedSearchReducer from './SlottedJobSearch/SlottedSearchReducer';
import MarketplaceMatchesReducer from './MarketplaceMatches/MarketplaceMatchesReducer';
import ParticipationMatchesReducer from './ParticipationMatches/ParticipationMatchesReducer';
import BulkPricing from './BulkPricing/BulkPricingReducer';
import AIMatching from './AIMatching/aiMatchReducer';

import {
  ADD_SURVEY_JOB_MATCH_FAILURE,
  ADD_SURVEY_JOB_MATCH_START,
  ADD_SURVEY_JOB_MATCH_SUCCESS,
  COPY_JOB_MATCHES_FAILED,
  COPY_JOB_MATCHES_START,
  COPY_JOB_MATCHES_SUCCESS,
  DELETE_JOB_MATCH_FAILED,
  DELETE_JOB_MATCH_START,
  DELETE_JOB_MATCH_SUCCESS,
  GET_SURVEY_SELECTED_VIEW_ERROR,
  GET_SURVEY_SELECTED_VIEW_START,
  GET_SURVEY_SELECTED_VIEW_SUCCESS,
  GET_SURVEY_VIEWS_ERROR,
  GET_SURVEY_VIEWS_START,
  GET_SURVEY_VIEWS_SUCCESS,
  SET_MARKET_PRICING_DISPLAY_IN,
  SET_SURVEY_SELECTED_VIEW_ERROR,
  SET_SURVEY_SELECTED_VIEW_START,
  RESET_MARKET_PRICING_REPORT_OPTION,
  SET_MARKET_PRICING_REPORT_OPTION,
  SET_SURVEY_SELECTED_VIEW_SUCCESS,
  UPLOAD_JOB_DESCRIPTION_START,
  UPLOAD_JOB_DESCRIPTION_SUCCESS,
  UPLOAD_JOB_DESCRIPTION_ERROR,
  DELETE_JOB_DESCRIPTION_START,
  DELETE_JOB_DESCRIPTION_SUCCESS,
  DELETE_JOB_DESCRIPTION_ERROR,
  DOWNLOAD_JOB_DESCRIPTION_START,
  DOWNLOAD_JOB_DESCRIPTION_SUCCESS,
  DOWNLOAD_JOB_DESCRIPTION_ERROR,
  RESET_JOB_DESCRIPTION,
  SET_JOB_DESCRIPTION_ERROR,
  CHANGE_STATUS_START,
  CHANGE_STATUS_SUCCESS,
  CHANGE_STATUS_ERROR,
  APPLY_WEIGHTING_START,
  APPLY_WEIGHTING_SUCCESS,
  APPLY_WEIGHTING_ERROR,
  APPLY_WEIGHTING_RESET,
  APPLY_WEIGHTING_COUNTS,
  DELETE_VIEW_START,
  DELETE_VIEW_SUCCESS,
  DELETE_VIEW_ERROR,
  MOVE_VIEW_START,
  MOVE_VIEW_SUCCESS,
  MOVE_VIEW_ERROR,
  GET_ELEMENT_TREE_START,
  GET_ELEMENT_TREE_SUCCESS,
  GET_ELEMENT_TREE_ERROR,
  GET_ELEMENT_TREE_RESET,
  SAVE_VIEW_START,
  SAVE_VIEW_SUCCESS,
  SAVE_VIEW_ERROR,
  SET_DEFAULT_VIEW_START,
  SET_DEFAULT_VIEW_SUCCESS,
  SET_DEFAULT_VIEW_ERROR,
  DATA_REFETCHED
} from './MarketPricingActions';
import { SESSION_DATA_FETCH_SUCCESS } from '../../actions/session';
import { DISPLAY_IN_TYPE } from '../../lib/displayIn';

const initialState = {
  adhocJobCode: null,
  isMatchDeleting: false,
  isMatchDeleteFailed: false,
  displayIn: DISPLAY_IN_TYPE.ANNUAL,
  isCopyingJobMatches: false,
  isCopyJobMatchesFailed: false,
  reportDropdownOption: null,
  surveyViews: {
    views: [],
    defaultViewId: null,
    loading: false,
    loaded: false
  },
  surveySelectedView: {
    selectedViewPk: null,
    loading: false,
    loaded: false
  },
  setSurveyView: {
    loading: false
  },
  surveyError: null,
  loadingId: null,
  refetchData: false,
  jobDescription: {
    fileType: undefined,
    downloadURL: undefined,
    previewURL: undefined,
    hasHRTMS: false
  },
  uploadJobDescriptionLoading: false,
  deleteJobDescriptionLoading: false,
  downloadJobDescriptionLoading: false,
  jobDescriptionError: undefined,
  changeStatus: {
    loading: false,
    error: undefined
  },
  applyWeighting: {
    loading: false,
    countWithZero: undefined,
    countWithoutZero: undefined,
    error: undefined,
    loaded: false
  },
  deleteView: {
    loading: false,
    error: undefined
  },
  moveView: {
    loading: false,
    error: undefined
  },
  elementTree: {
    data: [],
    defaultFields: [],
    currentFields: [],
    loading: false,
    loaded: false,
    error: undefined
  },
  saveView: {
    loading: false,
    error: undefined
  },
  setDefault: {
    loading: false,
    error: undefined
  }
};

function MarketPricingReducer(state = initialState, action = {}) {
  switch (action.type) {
    case CHANGE_STATUS_START: {
      return {
        ...state,
        changeStatus: {
          loading: true,
          error: undefined
        }
      };
    }
    case CHANGE_STATUS_SUCCESS: {
      return {
        ...state,
        changeStatus: {
          loading: false,
          error: undefined
        }
      };
    }
    case CHANGE_STATUS_ERROR: {
      return {
        ...state,
        changeStatus: {
          loading: false,
          error: action.error || 'failed to change status'
        }
      };
    }
    case ADD_SURVEY_JOB_MATCH_START: {
      return {
        ...state,
        loadingId: action.id
      };
    }
    case ADD_SURVEY_JOB_MATCH_SUCCESS: {
      return {
        ...state,
        adhocJobCode: action.payload.adhocJobCode,
        loadingId: null,
        refetchData: action.payload.refetchData
      };
    }
    case ADD_SURVEY_JOB_MATCH_FAILURE: {
      return {
        ...state,
        loadingId: null
      };
    }
    case DELETE_JOB_MATCH_START: {
      return {
        ...state,
        isMatchDeleting: true,
        loadingId: action.id
      };
    }
    case DELETE_JOB_MATCH_SUCCESS: {
      return {
        ...state,
        isMatchDeleting: false,
        isMatchDeleteFailed: false,
        loadingId: null,
        refetchData: action.payload.refetchData
      };
    }
    case DELETE_JOB_MATCH_FAILED: {
      return {
        ...state,
        isMatchDeleting: false,
        isMatchDeleteFailed: true,
        loadingId: null
      };
    }
    case DATA_REFETCHED: {
      return {
        ...state,
        refetchData: false
      };
    }
    case SET_MARKET_PRICING_DISPLAY_IN: {
      return {
        ...state,
        displayIn: action.displayIn
      };
    }
    case SET_MARKET_PRICING_REPORT_OPTION: {
      return {
        ...state,
        reportDropdownOption: action.option
      };
    }
    case RESET_MARKET_PRICING_REPORT_OPTION: {
      return {
        ...state,
        reportDropdownOption: null
      };
    }
    case COPY_JOB_MATCHES_START: {
      return {
        ...state,
        isCopyingJobMatches: true,
        loadingId: action.id
      };
    }
    case COPY_JOB_MATCHES_SUCCESS: {
      return {
        ...state,
        isCopyingJobMatches: false,
        isCopyJobMatchesFailed: false,
        loadingId: null
      };
    }
    case COPY_JOB_MATCHES_FAILED: {
      return {
        ...state,
        isCopyingJobMatches: false,
        isCopyJobMatchesFailed: true,
        loadingId: null
      };
    }
    case GET_SURVEY_VIEWS_START: {
      return {
        ...state,
        surveyViews: {
          views: [],
          defaultViewId: null,
          loading: true,
          loaded: false
        },
        surveyError: null
      };
    }
    case GET_SURVEY_VIEWS_SUCCESS: {
      return {
        ...state,
        surveyViews: {
          views: action.views,
          defaultViewId: action.defaultViewId,
          loading: false,
          loaded: true
        },
        surveyError: null
      };
    }
    case GET_SURVEY_VIEWS_ERROR: {
      return {
        ...state,
        surveyViews: {
          views: [],
          defaultViewId: null,
          loading: false,
          loaded: true
        },
        surveyError: action.message || 'Error retrieving survey views'
      };
    }
    case GET_SURVEY_SELECTED_VIEW_START: {
      return {
        ...state,
        surveySelectedView: {
          selectedViewPk: state.surveySelectedView.selectedViewPk,
          loading: true,
          loaded: false
        },
        surveyError: null
      };
    }
    case GET_SURVEY_SELECTED_VIEW_SUCCESS: {
      return {
        ...state,
        surveySelectedView: {
          selectedViewPk: action.selectedViewPk,
          loading: false,
          loaded: true
        },
        surveyError: null
      };
    }
    case GET_SURVEY_SELECTED_VIEW_ERROR: {
      return {
        ...state,
        surveySelectedView: {
          selectedViewPk: state.surveySelectedView.selectedViewPk,
          loading: false,
          loaded: true
        },
        surveyError: action.message || 'Error retrieving selected survey view'
      };
    }
    case SET_SURVEY_SELECTED_VIEW_START: {
      return {
        ...state,
        setSurveyView: {
          loading: true
        },
        surveyError: null
      };
    }
    case SET_SURVEY_SELECTED_VIEW_SUCCESS: {
      return {
        ...state,
        setSurveyView: {
          loading: false
        },
        surveySelectedView: {
          selectedViewPk: action.selectedViewPk,
          loading: false,
          loaded: true
        },
        surveyError: null
      };
    }
    case SET_SURVEY_SELECTED_VIEW_ERROR: {
      return {
        ...state,
        setSurveyView: {
          loading: false
        },
        surveyError: action.message || 'Error setting selected survey view'
      };
    }
    case UPLOAD_JOB_DESCRIPTION_START: {
      return {
        ...state,
        uploadJobDescriptionLoading: true,
        jobDescriptionError: undefined
      };
    }
    case UPLOAD_JOB_DESCRIPTION_SUCCESS: {
      return {
        ...state,
        uploadJobDescriptionLoading: false,
        jobDescriptionError: undefined
      };
    }
    case UPLOAD_JOB_DESCRIPTION_ERROR: {
      return {
        ...state,
        uploadJobDescriptionLoading: false,
        jobDescriptionError: action.error
      };
    }
    case DELETE_JOB_DESCRIPTION_START: {
      return {
        ...state,
        deleteJobDescriptionLoading: true,
        jobDescriptionError: undefined
      };
    }
    case DELETE_JOB_DESCRIPTION_SUCCESS: {
      return {
        ...state,
        deleteJobDescriptionLoading: false,
        jobDescription: {
          fileType: undefined,
          downloadURL: undefined,
          previewURL: undefined,
          hasHRTMS: false
        },
        jobDescriptionError: undefined
      };
    }
    case DELETE_JOB_DESCRIPTION_ERROR: {
      return {
        ...state,
        deleteJobDescriptionLoading: false,
        jobDescriptionError: action.error
      };
    }
    case DOWNLOAD_JOB_DESCRIPTION_START: {
      return {
        ...state,
        downloadJobDescriptionLoading: true,
        jobDescriptionError: undefined
      };
    }
    case DOWNLOAD_JOB_DESCRIPTION_SUCCESS: {
      return {
        ...state,
        downloadJobDescriptionLoading: false,
        jobDescription: {
          fileType: action.fileType,
          downloadURL: action.downloadURL,
          previewURL: action.previewURL,
          hasHRTMS: action.hasHRTMS
        },
        jobDescriptionError: undefined
      };
    }
    case DOWNLOAD_JOB_DESCRIPTION_ERROR: {
      return {
        ...state,
        downloadJobDescriptionLoading: false,
        jobDescription: {
          fileType: undefined,
          downloadURL: undefined,
          previewURL: undefined,
          hasHRTMS: false
        },
        jobDescriptionError: undefined
      };
    }
    case RESET_JOB_DESCRIPTION: {
      return {
        ...state,
        deleteJobDescriptionLoading: false,
        uploadJobDescriptionLoading: false,
        jobDescription: {
          fileType: undefined,
          downloadURL: undefined,
          previewURL: undefined,
          hasHRTMS: false
        },
        jobDescriptionError: undefined
      };
    }
    case SET_JOB_DESCRIPTION_ERROR: {
      return {
        ...state,
        jobDescriptionError: action.error
      };
    }
    case APPLY_WEIGHTING_START: {
      return {
        ...state,
        applyWeighting: {
          loading: true,
          countWithZero: state.applyWeighting.countWithZero,
          countWithoutZero: state.applyWeighting.countWithoutZero,
          error: undefined,
          loaded: false
        }
      };
    }
    case APPLY_WEIGHTING_SUCCESS: {
      return {
        ...state,
        applyWeighting: {
          loading: false,
          countWithZero: state.applyWeighting.countWithZero,
          countWithoutZero: state.applyWeighting.countWithoutZero,
          error: undefined,
          loaded: true
        }
      };
    }
    case APPLY_WEIGHTING_ERROR: {
      return {
        ...state,
        applyWeighting: {
          loading: false,
          countWithZero: undefined,
          countWithoutZero: undefined,
          error: action.error,
          loaded: true
        }
      };
    }
    case APPLY_WEIGHTING_RESET: {
      return {
        ...state,
        applyWeighting: {
          loading: false,
          countWithZero: undefined,
          countWithoutZero: undefined,
          error: undefined,
          loaded: false
        }
      };
    }
    case APPLY_WEIGHTING_COUNTS: {
      return {
        ...state,
        applyWeighting: {
          loading: false,
          countWithZero: action.countWithZero,
          countWithoutZero: action.countWithoutZero,
          error: undefined,
          loaded: false
        }
      };
    }
    case DELETE_VIEW_START: {
      return {
        ...state,
        deleteView: {
          loading: true,
          error: undefined
        }
      };
    }
    case DELETE_VIEW_SUCCESS: {
      return {
        ...state,
        deleteView: {
          loading: false,
          error: undefined
        }
      };
    }
    case DELETE_VIEW_ERROR: {
      return {
        ...state,
        deleteView: {
          loading: false,
          error: action.error
        }
      };
    }
    case MOVE_VIEW_START: {
      return {
        ...state,
        moveView: {
          loading: true,
          error: undefined
        }
      };
    }
    case MOVE_VIEW_SUCCESS: {
      return {
        ...state,
        moveView: {
          loading: false,
          error: undefined
        }
      };
    }
    case MOVE_VIEW_ERROR: {
      return {
        ...state,
        moveView: {
          loading: false,
          error: action.error
        }
      };
    }
    case GET_ELEMENT_TREE_START: {
      return {
        ...state,
        elementTree: {
          data: null,
          defaultFields: null,
          currentFields: null,
          loading: true,
          loaded: false,
          error: undefined
        }
      };
    }
    case GET_ELEMENT_TREE_SUCCESS: {
      return {
        ...state,
        elementTree: {
          data: action.elementTree,
          defaultFields: action.defaultFields,
          currentFields: action.currentFields,
          loading: false,
          loaded: true,
          error: undefined
        }
      };
    }
    case GET_ELEMENT_TREE_ERROR: {
      return {
        ...state,
        elementTree: {
          data: null,
          defaultFields: null,
          currentFields: null,
          loading: false,
          loaded: true,
          error: action.error
        }
      };
    }
    case GET_ELEMENT_TREE_RESET: {
      return {
        ...state,
        elementTree: {
          data: null,
          defaultFields: null,
          currentFields: null,
          loading: false,
          loaded: false,
          error: null
        }
      };
    }
    case SAVE_VIEW_START: {
      return {
        ...state,
        saveView: {
          loading: true,
          error: undefined
        }
      };
    }
    case SAVE_VIEW_SUCCESS: {
      return {
        ...state,
        saveView: {
          loading: false,
          error: undefined
        }
      };
    }
    case SAVE_VIEW_ERROR: {
      return {
        ...state,
        saveView: {
          loading: false,
          error: action.error
        }
      };
    }
    case SET_DEFAULT_VIEW_START: {
      return {
        ...state,
        setDefault: {
          loading: true,
          error: undefined
        }
      };
    }
    case SET_DEFAULT_VIEW_SUCCESS: {
      return {
        ...state,
        setDefault: {
          loading: false,
          error: undefined
        }
      };
    }
    case SET_DEFAULT_VIEW_ERROR: {
      return {
        ...state,
        setDefault: {
          loading: false,
          error: action.error
        }
      };
    }
    case SESSION_DATA_FETCH_SUCCESS: {
      const displayIn = Object.values(DISPLAY_IN_TYPE).find(TYPE => {
        return TYPE === action.data.monetary_display;
      });
      return displayIn ? { ...state, displayIn } : state;
    }
    default: {
      return {
        ...state
      };
    }
  }
}

export default combineReducers({
  MarketPricingReducer,
  JobPricingReducer,
  InternalJobsReducer,
  SurveyLibraryReducer,
  SlottedSearchReducer,
  MarketplaceMatchesReducer,
  ParticipationMatchesReducer,
  BulkPricing,
  AIMatching
});
